import type { FieldDefinition, SchemaResponse } from '../types';
import { isCompanySource } from './entityLabels';

export interface ExportConfig {
  export_fields: string[];
  company_export_fields: string[];
  allowed_export_fields?: string[];
  company_allowed_export_fields?: string[];
}

export interface ExportFieldGroup {
  name: string;
  fields: { name: string; definition: FieldDefinition }[];
}

export function exportDefaults(config: ExportConfig, source: string): string[] {
  return isCompanySource(source) ? config.company_export_fields : config.export_fields;
}

export function exportFieldGroups(schema: SchemaResponse, config: ExportConfig, source: string): ExportFieldGroup[] {
  const allowed = isCompanySource(source)
    ? config.company_allowed_export_fields ?? config.company_export_fields
    : config.allowed_export_fields ?? config.export_fields;
  const sections = schema.data_sources?.[source]?.sections ?? {};
  const groups = new Map<string, ExportFieldGroup>();
  for (const name of new Set(allowed)) {
    const section = Object.entries(sections).find(([, fields]) => fields.includes(name))?.[0]
      ?? (isCompanySource(source) ? 'Company' : 'Contact');
    const definition: FieldDefinition = schema.field_definitions?.[name] ?? {
      label: name.toLowerCase().split('_').map(word => word[0].toUpperCase() + word.slice(1)).join(' '),
      type: 'keyword', priority: 0, sortable: false, section
    };
    if (!groups.has(section)) groups.set(section, { name: section, fields: [] });
    groups.get(section)!.fields.push({ name, definition });
  }
  return Array.from(groups.values());
}

export function buildExportRequest(source: string, format: 'csv' | 'json', selectAll: boolean,
  ids: string[], query: string | undefined, completeFilters?: Record<string, unknown>, fields?: string[]) {
  const request: Record<string, unknown> = { data_source: source, format, select_all: selectAll };
  if (fields?.length) request.export_fields = fields;
  if (selectAll) {
    const filters = { ...completeFilters };
    for (const flag of ['group_contact_keyword_fields', 'group_company_keyword_fields']) {
      if (filters[flag] !== undefined) request[flag] = filters[flag];
      delete filters[flag];
    }
    if (Object.keys(filters).length) request.filters = filters;
    if (query) request.query = query;
  } else if (ids.length) {
    request[source === 'accounts' ? 'company_ids' : 'contact_uuids'] = ids;
  }
  return request;
}
